import React, { useMemo } from "react"
import { createStyles, View } from "@design-system/core"
import { __DEV__ } from "@design-system/utils"
import { LayerGroupsProps, PortalLayer } from "./types"

const styles = createStyles(() => ({
  fixedLayerGroup: {
    backfaceVisibility: "hidden",
    left: 0,
    position: "fixed",
    right: 0,
  },
  inFlowLayerGroup: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
  },
  topPositionedLayer: {
    top: 0,
  },
  bottomPositionedLayer: {
    bottom: 0,
  },
}))

export function LayerGroups(props: LayerGroupsProps) {
  const { layers, isFixed } = props

  // group layers by zIndex,
  // sort groups by zIndex
  const groups = useMemo(() => {
    const groupsByZIndex = layers.reduce((groups, layer) => {
      const layers = groups.get(layer.zIndex) || []
      layers.push(layer)
      groups.set(layer.zIndex, layers)
      return groups
    }, new Map<number, PortalLayer[]>())

    return Array.from(groupsByZIndex.keys())
      .sort()
      .map((zIndex) => ({
        zIndex,
        layerGroup: groupsByZIndex
          .get(zIndex)!
          .sort(
            (layer1, layer2) =>
              layer1.order - layer2.order || layer1.id - layer2.id,
          ),
      }))
  }, [layers])

  return (
    <>
      {groups.map(({ zIndex, layerGroup }) => (
        <View
          key={`layerGroup-${zIndex}`}
          testID={`layerGroup-${zIndex}`}
          style={[
            isFixed && styles.fixedLayerGroup,
            !isFixed && styles.inFlowLayerGroup,
            { zIndex },
          ]}
          pointerEvents={isFixed ? "box-none" : "auto"}
        >
          {layerGroup.map((layer) => (
            <View
              pointerEvents="box-none"
              testID={`layer-${layer.id}`}
              key={layer.id}
              ref={layer.onRef}
            />
          ))}
        </View>
      ))}
    </>
  )
}

if (__DEV__) {
  LayerGroups.displayName = "PortalLayerGroups"
}
